import { Slot, Slottable } from "@radix-ui/react-slot";
import { cloneElement, forwardRef, Fragment, isValidElement, type ReactElement, type ReactNode } from "react";

type TextWrapperProps = {
    children: ReactNode;
    asChild?: boolean;
    disabled?: boolean;
    leftElement?: ReactNode;
    rightElement?: ReactNode;
    className?: string;
    disableTracking?: boolean;
    _disableTranslateWrapping?: boolean;
    as?: React.ElementType;
};

/**
 * Component that handles text wrapping for Google Translate issues.
 */
export const TextWrapper = forwardRef<HTMLElement, TextWrapperProps>(
    (
        {
            children,
            asChild,
            leftElement,
            rightElement,
            className,
            disableTracking,
            _disableTranslateWrapping,
            as: Comp = "button",
            ...props
        },
        ref,
    ) => {
        let content: ReactNode = children;

        if (!_disableTranslateWrapping) {
            // Wrap text content in a span to prevent G translate issues
            content = wrapTextChildren(children);
            if (asChild && isValidElement(children)) {
                return (
                    <Slot
                        ref={ref}
                        className={className}
                        data-track-disable={disableTracking ? "true" : undefined}
                        {...props}
                    >
                        {cloneElement(
                            children,
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                            children.props,
                            <>
                                {leftElement}
                                <span className="flex items-center gap-1">{children.props.children}</span>
                            </>,
                        )}
                    </Slot>
                );
            }
        }

        return (
            <Comp ref={ref} className={className} data-track-disable={disableTracking ? "true" : undefined} {...props}>
                {leftElement}
                <Slottable>{content}</Slottable>
                {rightElement}
            </Comp>
        );
    },
);

TextWrapper.displayName = "TextWrapper";

/**
 * Wraps children in spans to prevent Google Translate issues.
 * Handles both single children and arrays of children.
 */
function wrapTextChildren(children: ReactNode): ReactNode {
    return Array.isArray(children)
        ? children.map((child: ReactNode, i: number) => wrapInSpan(child, `wrapped-child-${i}`))
        : wrapInSpan(children, "wrapped-child");
}

function wrapInSpan(node: ReactNode, key: string): ReactNode {
    if (typeof node === "string") {
        return (
            <span key={key} className="truncate">
                {node}
            </span>
        );
    }
    if (isValidElement<ReactElement>(node) && node.type === Fragment) {
        return (
            <span key={key} className="truncate">
                {node}
            </span>
        );
    }
    return node;
}
